import { LogisticsProjectLookupComponent } from '../../../logistics-projects/logistics-project-lookup.component';
import * as XLSX from 'xlsx';
import { Component, OnInit } from '@angular/core';
import { LazyLoadEvent, MenuItem, MessageService } from 'primeng/api';
import { AccountingReportsService, ReportDiagnostic } from "../../accounting-reports.service";
import { TaxReport } from "./tax-report.model"; // Ensure this model is updated
import { SessionStorageService } from "../../../services/session-storage.service";
import { TableLazyLoadEvent, TableModule } from "primeng/table";
import { CommonModule, CurrencyPipe } from "@angular/common";
import { FormsModule } from '@angular/forms';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { DatePickerModule } from 'primeng/datepicker';
import { ProgressBarModule } from 'primeng/progressbar';
import { ToastModule } from 'primeng/toast';
import { DividerModule } from "primeng/divider";
import { Router } from '@angular/router';
import { IftaLabelModule } from 'primeng/iftalabel';
import { SelectModule } from 'primeng/select';
import { PageDataPanelComponent } from '../../../shared/page-layout/page-data-panel.component';
import { PageFormSectionComponent } from '../../../shared/page-layout/page-form-section.component';
import { PageHeroComponent } from '../../../shared/page-layout/page-hero.component';
import { PageShellComponent } from '../../../shared/page-layout/page-shell.component';
import { ReportEmailComponent } from '../shared/report-email.component';
import { closeFinancialReportPdf, downloadFinancialReportPdf, openFinancialReportPdf } from '../shared/financial-report-pdf';
import { finalize } from 'rxjs';
import { PendingActionTracker } from '../../../shared/state/pending-action-tracker';

@Component({
    selector: 'app-tax-report',
    templateUrl: './tax-report.component.html',
    styleUrls: ['./tax-report.component.css'],
    standalone: true,
    imports: [
        LogisticsProjectLookupComponent,
        CommonModule,
        FormsModule,
        BreadcrumbModule,
        ButtonModule,
        TableModule,
        DatePickerModule,
        ToastModule,
        CardModule,
        ProgressBarModule,
        DividerModule,
        IftaLabelModule,
        SelectModule,
        PageShellComponent,
        PageHeroComponent,
        PageFormSectionComponent,
        PageDataPanelComponent,
        ReportEmailComponent
    ],
    providers: [
        MessageService,
        AccountingReportsService,
        CurrencyPipe,
        SessionStorageService
    ]
})
export class TaxReportComponent implements OnInit {
    readonly reportActions = new PendingActionTracker<string>();

    taxReportItems: TaxReport[] = []; // Holds all fetched data
    vatOnSalesItems: TaxReport[] = [];
    vatOnPurchasesItems: TaxReport[] = [];
    vatOnAdjustmentsOtherItems: TaxReport[] = []; // For 'VAT on Journal Entry' and 'Other VAT Items'

    isLoading: boolean = false;
    totalRecords: number = 0; // Total records across all categories
    first: number = 0;

    startDate: Date;
    endDate: Date;

    breadcrumbItems: MenuItem[];
    homeBreadcrumb: MenuItem;

    // Properties for Tax Report totals (Grand Totals)
    totalVATCollectedSum: number = 0;
    totalVATPaidSum: number = 0;
    netVATSum: number = 0;
    totalAmountSARSum: number = 0;
    totalAdjustmentSARSum: number = 0;
    reportCurrencyCode: string = 'SR'; // Default, will be updated based on data
    diagnostics: ReportDiagnostic[] = [];

    selectedLogisticsProjectId?: number | null;

    constructor(private accountReportService: AccountingReportsService,
        private sessionStorageService: SessionStorageService,
        private messageService: MessageService,
        private currencyPipe: CurrencyPipe,
        private router: Router) { }

    ngOnInit(): void {
        const today = new Date();
        // Set initial dates for current month
        this.startDate = new Date(today.getFullYear(), today.getMonth(), 1);
        this.endDate = new Date(today.getFullYear(), today.getMonth() + 1, 0);

        this.breadcrumbItems = [{ label: 'Accounts' }, { label: 'Reports' }, { label: 'Tax Report' }];
        this.homeBreadcrumb = { icon: 'pi pi-home', routerLink: '/' };

        this.loadTaxReportData({});
    }

    loadTaxReportData(event: TableLazyLoadEvent) {
        if (!this.startDate || !this.endDate) {
            this.messageService.add({ severity: 'warn', summary: 'Missing Dates', detail: 'Please select both start and end dates.' });
            return;
        }

        this.isLoading = true;

        this.accountReportService.getTaxReportV2(
            this.sessionStorageService.getCompany()?.id ?? 0,
            this.startDate.getTime(),
            this.endDate.getTime(),
            this.selectedLogisticsProjectId
        ).subscribe(
            (response) => {
                const data = response.rows ?? [];
                this.diagnostics = response.diagnostics ?? [];
                this.taxReportItems = data;
                this.totalRecords = data.length;

                if (data.length > 0) {
                    this.reportCurrencyCode = data[0].currencyCode;
                } else {
                    this.reportCurrencyCode = 'SR'; // Fallback
                }

                this.filterTaxReportItems(); // Filter data into specific arrays
                this.calculateTotals(); // Calculate grand totals for summary cards
                this.isLoading = false;
            },
            error => {
                console.error('Error fetching Tax Report data:', error);
                this.isLoading = false;
                this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to load Tax Report data.' });
                // Reset all data and totals on error
                this.taxReportItems = [];
                this.vatOnSalesItems = [];
                this.vatOnPurchasesItems = [];
                this.vatOnAdjustmentsOtherItems = [];
                this.totalVATCollectedSum = 0;
                this.totalVATPaidSum = 0;
                this.netVATSum = 0;
                this.totalAmountSARSum = 0;
                this.totalAdjustmentSARSum = 0;
                this.diagnostics = [];
            }
        );
    }

    filterTaxReportItems(): void {
        this.vatOnSalesItems = this.taxReportItems.filter(item =>
            item.vatCategory === 'VAT on Sales' ||
            item.vatCategory === 'Exempted Sales VAT' ||
            item.vatCategory === 'Zero-rated Sales VAT'
        ).sort((a, b) => a.sortOrder - b.sortOrder); // Sort within category

        this.vatOnPurchasesItems = this.taxReportItems.filter(item =>
            item.vatCategory === 'VAT on Purchases' ||
            item.vatCategory === 'Exempted Purchases VAT' ||
            item.vatCategory === 'Zero-rated Purchases VAT'
        ).sort((a, b) => a.sortOrder - b.sortOrder);

        this.vatOnAdjustmentsOtherItems = this.taxReportItems.filter(item =>
            item.vatCategory === 'VAT on Journal Entry' ||
            item.vatCategory === 'Other VAT Items'
        ).sort((a, b) => a.sortOrder - b.sortOrder);
    }

    calculateTotals(): void {
        let collected = 0;
        let paid = 0;
        let net = 0;
        let amount = 0;
        let adjustment = 0;

        this.taxReportItems.forEach(item => {
            // Convert BigDecimal from Java to number for calculations
            collected += parseFloat(item.vatCollected.toString());
            paid += parseFloat(item.vatPaid.toString());
            net += parseFloat((item.netVAT ?? 0).toString());
            amount += parseFloat(item.amountSAR.toString());
            adjustment += parseFloat(item.adjustmentSAR.toString());
        });

        this.totalAmountSARSum = amount;
        this.totalAdjustmentSARSum = adjustment;
        this.totalVATCollectedSum = collected;
        this.totalVATPaidSum = paid;
        this.netVATSum = net;

        console.log('Total Amount SAR:', this.totalAmountSARSum);
        console.log('Total Adjustment SAR:', this.totalAdjustmentSARSum);
        console.log('Total VAT Collected Sum:', this.totalVATCollectedSum);
        console.log('Total VAT Paid Sum:', this.totalVATPaidSum);
        console.log('Net VAT Sum (Overall):', this.netVATSum);
    }

    // Helper function to get total for a specific category and column
    getCategoryTotal(category: string, column: 'amountSAR' | 'adjustmentSAR' | 'vatCollected' | 'vatPaid' | 'netVAT'): number {
        // This function needs to sum across all relevant categories if 'Other' is selected
        let itemsToSum: TaxReport[] = [];
        if (category === 'VAT on Sales') {
            itemsToSum = this.vatOnSalesItems;
        } else if (category === 'VAT on Purchases') {
            itemsToSum = this.vatOnPurchasesItems;
        } else if (category === 'VAT on Journal Entry' || category === 'Other VAT Items') {
            // Sum for the "Adjustments / Other" table footer
            itemsToSum = this.vatOnAdjustmentsOtherItems;
        }

        return itemsToSum
            .reduce((sum, item) => sum + parseFloat((item[column] ?? 0).toString()), 0);
    }

    exportExcel(): void {
        if (!this.taxReportItems || this.taxReportItems.length === 0) {
            this.messageService.add({ severity: 'warn', summary: 'No Data', detail: 'No data to export.' });
            return;
        }

        const workbook = XLSX.utils.book_new();

        // Helper to add a sheet
        const addSheet = (sheetName: string, data: TaxReport[], title: string) => {
            const header = ['VAT SubCategory', 'Amount', 'Adjustment', 'VAT Collected', 'VAT Paid', 'Net VAT', 'Currency'];
            const sheetData = [
                [title], // Title row
                [], // Empty row for spacing
                header,
                ...data.map(item => [
                    item.vatSubCategory,
                    Number(item.amountSAR) || 0,
                    Number(item.adjustmentSAR) || 0,
                    Number(item.vatCollected) || 0,
                    Number(item.vatPaid) || 0,
                    Number(item.netVAT) || 0,
                    item.currencyCode
                ])
            ];

            // Add category total row
            const categoryTotalRow: any[] = [];
            categoryTotalRow.push(`Total ${title}:`);
            categoryTotalRow.push(this.getCategoryTotal(title.replace('Total ', ''), 'amountSAR')); // Adjust title to match category
            categoryTotalRow.push(this.getCategoryTotal(title.replace('Total ', ''), 'adjustmentSAR'));
            categoryTotalRow.push(this.getCategoryTotal(title.replace('Total ', ''), 'vatCollected'));
            categoryTotalRow.push(this.getCategoryTotal(title.replace('Total ', ''), 'vatPaid'));
            categoryTotalRow.push(this.getCategoryTotal(title.replace('Total ', ''), 'netVAT'));
            categoryTotalRow.push(this.reportCurrencyCode);
            sheetData.push(categoryTotalRow);

            const worksheet: XLSX.WorkSheet = XLSX.utils.aoa_to_sheet(sheetData);

            // Set column widths
            const wscols = [
                { wch: 30 }, // VAT SubCategory
                { wch: 18 }, // Amount
                { wch: 18 }, // Adjustment
                { wch: 18 }, // VAT Collected
                { wch: 18 }, // VAT Paid
                { wch: 18 }, // Net VAT
                { wch: 10 }  // Currency
            ];
            worksheet['!cols'] = wscols;

            // Merge cells for the title
            worksheet['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: header.length - 1 } }];
            // Style the title cell (bold)
            if (worksheet['A1']) {
                worksheet['A1'].s = { font: { bold: true, sz: 14 } };
            }

            XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
        };

        // Add sheets for each category
        addSheet('VAT on Sales', this.vatOnSalesItems, 'VAT on Sales');
        addSheet('VAT on Purchases', this.vatOnPurchasesItems, 'VAT on Purchases');
        addSheet('VAT Adjustments Or Other', this.vatOnAdjustmentsOtherItems, 'VAT on Adjustments / Other VAT Items');

        // Add a summary sheet (optional, but good for overview)
        const summarySheetData = [
            ['Tax Report Summary'],
            [],
            ['Category', 'Total Amount', 'Total Adjustment', 'Total VAT Collected', 'Total VAT Paid', 'Net VAT', 'Currency'],
            ['VAT on Sales', this.getCategoryTotal('VAT on Sales', 'amountSAR'), this.getCategoryTotal('VAT on Sales', 'adjustmentSAR'), this.getCategoryTotal('VAT on Sales', 'vatCollected'), this.getCategoryTotal('VAT on Sales', 'vatPaid'), this.getCategoryTotal('VAT on Sales', 'netVAT'), this.reportCurrencyCode],
            ['VAT on Purchases', this.getCategoryTotal('VAT on Purchases', 'amountSAR'), this.getCategoryTotal('VAT on Purchases', 'adjustmentSAR'), this.getCategoryTotal('VAT on Purchases', 'vatCollected'), this.getCategoryTotal('VAT on Purchases', 'vatPaid'), this.getCategoryTotal('VAT on Purchases', 'netVAT'), this.reportCurrencyCode],
            ['VAT on Adjustments / Other', this.getCategoryTotal('VAT on Journal Entry', 'amountSAR') + this.getCategoryTotal('Other VAT Items', 'amountSAR'), this.getCategoryTotal('VAT on Journal Entry', 'adjustmentSAR') + this.getCategoryTotal('Other VAT Items', 'adjustmentSAR'), this.getCategoryTotal('VAT on Journal Entry', 'vatCollected') + this.getCategoryTotal('Other VAT Items', 'vatCollected'), this.getCategoryTotal('VAT on Journal Entry', 'vatPaid') + this.getCategoryTotal('Other VAT Items', 'vatPaid'), this.getCategoryTotal('VAT on Journal Entry', 'netVAT') + this.getCategoryTotal('Other VAT Items', 'netVAT'), this.reportCurrencyCode],
            [],
            ['Grand Totals:', this.totalAmountSARSum, this.totalAdjustmentSARSum, this.totalVATCollectedSum, this.totalVATPaidSum, this.netVATSum, this.reportCurrencyCode]
        ];
        const summaryWorksheet: XLSX.WorkSheet = XLSX.utils.aoa_to_sheet(summarySheetData);
        summaryWorksheet['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: summarySheetData[2].length - 1 } }];
        if (summaryWorksheet['A1']) {
            summaryWorksheet['A1'].s = { font: { bold: true, sz: 16 } };
        }
        XLSX.utils.book_append_sheet(workbook, summaryWorksheet, 'Summary');

        const excelBuffer: any = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
        this.saveAsExcelFile(excelBuffer, "TaxReport");
        this.messageService.add({ severity: 'success', summary: 'Exported', detail: 'Report exported to Excel.' });
    }

    private saveAsExcelFile(buffer: any, fileName: string): void {
        const data: Blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8' });
        const downloadLink: HTMLAnchorElement = document.createElement('a');
        const url = URL.createObjectURL(data);
        downloadLink.href = url;
        downloadLink.download = fileName + '_' + new Date().toISOString().slice(0, 10) + '.xlsx';
        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);
        URL.revokeObjectURL(url);
    }

    printReport(download = false): void {
        const action = download ? 'download' : 'print';
        if (!this.reportActions.begin(action)) return;
        const target = download ? null : window.open('', '_blank');
        this.accountReportService.printFinanceReport('tax-report', this.sessionStorageService.getCompany()?.id ?? 0, {
            startDate: this.startDate,
            endDate: this.endDate,
            logisticsProjectId: this.selectedLogisticsProjectId
        }).pipe(finalize(() => this.reportActions.end(action))).subscribe({
            next: blob => download ? downloadFinancialReportPdf(blob, 'tax-report') : openFinancialReportPdf(blob, target),
            error: () => {
                closeFinancialReportPdf(target);
                this.messageService.add({ severity: 'error', summary: 'Print unavailable', detail: 'Failed to generate the Tax Report PDF.' });
            }
        });
    }

    private legacyPrintReport(): void {
        if (this.taxReportItems.length === 0) {
            this.messageService.add({ severity: 'warn', summary: 'No Data', detail: 'There is no data to print.' });
            return;
        }

        // 1. Gather all necessary information
        const company = this.sessionStorageService.getCompany();
        const user = this.sessionStorageService.getUser(); // Assuming getUser() is the correct method
        const generationDate = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
        const fileName = 'TaxReport_' + new Date().toISOString().slice(0, 10);

        // Helper to safely format currency
        const formatCurrency = (value: number) => {
            return this.currencyPipe.transform(value, this.reportCurrencyCode, 'symbol', '1.2-2') || '';
        };

        // 2. Define the CSS for a clean print layout
        const styles = `
    <style>
        @media print {
            body { -webkit-print-color-adjust: exact; }
        }
        body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol"; font-size: 12px; }
        .report-header { display: flex; justify-content: space-between; align-items: center; border-bottom: 2px solid #666; padding-bottom: 10px; margin-bottom: 20px; }
        .report-header .logo { max-height: 50px; }
        .report-header .company-info { text-align: left; }
        .report-header .user-info { display: flex; align-items: center; text-align: right; }
        .report-header .user-info img { width: 40px; height: 40px; border-radius: 50%; margin-left: 10px; }
        .report-title { text-align: center; margin-bottom: 20px; }
        .summary-table, .data-table { width: 100%; border-collapse: collapse; margin-bottom: 25px; }
        .summary-table td { padding: 8px; border: 1px solid #ddd; }
        .data-table th, .data-table td { border: 1px solid #ccc; padding: 6px; text-align: left; }
        .data-table th { background-color: #f2f2f2; font-weight: bold; }
        .data-table .section-title { font-size: 1.2em; font-weight: bold; background-color: #e0e0e0; }
        .data-table .total-row { font-weight: bold; background-color: #f2f2f2; }
        .text-right { text-align: right !important; }
        .font-bold { font-weight: bold; }
    </style>
`;

        // 3. Build the HTML structure for the report
        let reportHtml = `
    <html>
        <head>
            <title>${fileName}</title>
            ${styles}
        </head>
        <body>
            <div class="report-header">
                <div class="company-info">
                    <img src="${company?.logo || 'https://placehold.co/150x50'}" alt="Company Logo" class="logo">
                    <h1>${company?.name || 'Your Company'}</h1>
                </div>
                <div class="user-info">
                    <div>
                        <strong>Printed By:</strong> ${user?.name || 'N/A'}<br>
                        <strong>Date:</strong> ${generationDate}
                    </div>
                    <img src="${user?.imageUrl || 'https://placehold.co/40x40'}" alt="User Avatar">
                </div>
            </div>

            <div class="report-title"><h1>Tax Report</h1></div>

            <table class="summary-table">
                <tr><td>Total VAT Collected</td><td class="text-right">${formatCurrency(this.totalVATCollectedSum)}</td></tr>
                <tr><td>Total VAT Paid</td><td class="text-right">${formatCurrency(this.totalVATPaidSum)}</td></tr>
                <tr><td class="font-bold">Net VAT Due</td><td class="text-right font-bold">${formatCurrency(this.netVATSum)}</td></tr>
            </table>
`;

        // Reusable helper function to build an HTML table for a section
        const buildSectionHtml = (title: string, data: TaxReport[], categoryForTotal: string): string => {
            if (data.length === 0) return '';

            let tableHtml = `<table class="data-table">`;
            // Section Header
            tableHtml += `<thead><tr><th colspan="6" class="section-title">${title}</th></tr>`;
            // Column Headers
            tableHtml += `<tr>
                        <th style="width: 30%;">VAT SubCategory</th>
                        <th class="text-right">Amount</th>
                        <th class="text-right">Adjustment</th>
                        <th class="text-right">VAT Collected</th>
                        <th class="text-right">VAT Paid</th>
                        <th class="text-right">Net VAT</th>
                      </tr></thead><tbody>`;

            // Data Rows
            data.forEach(item => {
                tableHtml += `
                <tr class="${item.vatSubCategory.toLowerCase().includes('total') ? 'font-bold' : ''}">
                    <td>${item.vatSubCategory}</td>
                    <td class="text-right">${formatCurrency(item.amountSAR)}</td>
                    <td class="text-right">${formatCurrency(item.adjustmentSAR)}</td>
                    <td class="text-right">${formatCurrency(item.vatCollected)}</td>
                    <td class="text-right">${formatCurrency(item.vatPaid)}</td>
                    <td class="text-right">${formatCurrency(item.netVAT ?? 0)}</td>
                </tr>`;
            });

            // Footer Total Row
            tableHtml += `</tbody><tfoot><tr class="total-row">
                    <td class="font-bold text-right">Total ${title}:</td>
                    <td class="text-right font-bold">${formatCurrency(this.getCategoryTotal(categoryForTotal, 'amountSAR'))}</td>
                    <td class="text-right font-bold">${formatCurrency(this.getCategoryTotal(categoryForTotal, 'adjustmentSAR'))}</td>
                    <td class="text-right font-bold">${formatCurrency(this.getCategoryTotal(categoryForTotal, 'vatCollected'))}</td>
                    <td class="text-right font-bold">${formatCurrency(this.getCategoryTotal(categoryForTotal, 'vatPaid'))}</td>
                    <td class="text-right font-bold">${formatCurrency(this.getCategoryTotal(categoryForTotal, 'netVAT'))}</td>
                  </tr></tfoot></table>`;
            return tableHtml;
        };

        // Add each section to the report
        reportHtml += buildSectionHtml('VAT on Sales', this.vatOnSalesItems, 'VAT on Sales');
        reportHtml += buildSectionHtml('VAT on Purchases', this.vatOnPurchasesItems, 'VAT on Purchases');
        reportHtml += buildSectionHtml('VAT on Adjustments / Other VAT Items', this.vatOnAdjustmentsOtherItems, 'VAT on Journal Entry'); // Use one of the categories for the total

        // Grand Totals Table
        reportHtml += `
        <table class="summary-table">
             <tr class="font-bold">
                <td>Grand Totals</td>
                <td class="text-right">${formatCurrency(this.totalAmountSARSum)}</td>
                <td class="text-right">${formatCurrency(this.totalAdjustmentSARSum)}</td>
                <td class="text-right">${formatCurrency(this.totalVATCollectedSum)}</td>
                <td class="text-right">${formatCurrency(this.totalVATPaidSum)}</td>
                <td class="text-right">${formatCurrency(this.netVATSum)}</td>
             </tr>
        </table>
    `;

        reportHtml += `</body></html>`;

        // 4. Open a new window and print the generated HTML
        const printWindow = window.open('', '_blank', 'height=800,width=1000');
        if (printWindow) {
            printWindow.document.write(reportHtml);
            printWindow.document.close();
            printWindow.focus(); // Necessary for some browsers
            setTimeout(() => { // Timeout to ensure content is rendered before printing
                printWindow.print();
                printWindow.close();
            }, 500);
        }

        this.messageService.add({ severity: 'info', summary: 'Print', detail: 'Print dialog initiated.' });
    }

    openVatTrace(item: TaxReport): void {
        if (!item.sourceDocumentType || !item.sourceDocumentId) {
            this.messageService.add({ severity: 'info', summary: 'Trace not available', detail: 'This VAT row has no source document drill-down yet.' });
            return;
        }
        const type = item.sourceDocumentType.toUpperCase();
        if (type === 'INVOICE') this.router.navigate(['/accounts/enterprise/receivables/generated-invoices/view', item.sourceDocumentId]);
        else if (type === 'DEBIT_NOTE') this.router.navigate(['/accounts/enterprise/receivables/debit-note/view', item.sourceDocumentId]);
        else if (type === 'CREDIT_NOTE') this.router.navigate(['/accounts/enterprise/payables/credit-note/view', item.sourceDocumentId]);
        else if (item.journalEntryId) this.router.navigate(['/accounts/enterprise/journal-entries/edit', item.journalEntryId]);
    }
}
